/**
 * @file Client-side StrKey shape validation. Doesn't need the full
 * Stellar SDK on the browser — a regex + length check rejects all
 * obviously-bad input. Real cryptographic validation happens server-side
 * (data-api: lib/strkey.ts via @stellar/stellar-sdk StrKey).
 *
 * This module exists so the landing-page input can give immediate
 * feedback without a network round-trip.
 */

export type AddressType = "G" | "C";

export interface ValidShape {
  ok: true;
  type: AddressType;
  address: string;
}

export interface InvalidShape {
  ok: false;
  reason: string;
}

export type ShapeResult = ValidShape | InvalidShape;

const STRKEY_RE = /^[A-Z2-7]{56}$/;

export function validateAddressShape(input: string): ShapeResult {
  const trimmed = input.trim();
  if (trimmed.length === 0) return { ok: false, reason: "Enter an address." };
  if (trimmed.length !== 56) return { ok: false, reason: "Address must be 56 characters." };
  if (!STRKEY_RE.test(trimmed)) return { ok: false, reason: "Address contains invalid characters." };
  const prefix = trimmed[0];
  if (prefix === "G") return { ok: true, type: "G", address: trimmed };
  if (prefix === "C") return { ok: true, type: "C", address: trimmed };
  return { ok: false, reason: `Use a G-address (account) or C-address (contract).` };
}

export function truncateAddress(address: string, head = 6, tail = 4): string {
  if (address.length <= head + tail + 3) return address;
  return `${address.slice(0, head)}…${address.slice(-tail)}`;
}
